
/** A zero-parameter closure representing one named event cause for a calculator. Returns the
 * Date when the event would occur, or undefined if the factor does not apply in this state. */
export type TAvailabilityFactor = () => Date | undefined;

/** A dated event factor with a human-readable label explaining what the event represents. */
export type TLabeledEventFactor = {
    date: Date;
    /** e.g. "day boundary", "credit threshold", "requirement met", "workday reset" */
    label: string;
};

/**
 * Per-factor result snapshot included in computeXxxResult return types.
 * Exposes the individual date each factor computed (before the minimum is taken).
 *
 * Limitation metadata (conservativeReason, returnsNonFutureDateReason, notStableReason,
 * notTestedAsWinnerReason) lives in the test-side FACTOR_LIMITATIONS registry
 * (tests/helpers/factorLimitations.ts), not here. Production code has no knowledge
 * of which factors are conservative or unstable; the regression checker reads the
 * registry directly.
 */
export type TFactorSnapshot = {
    date: Date | undefined;
};

/** Factor map keyed by canonical factor name, included in all computeXxxResult return types. */
export type TFactorMap = Record<string, TFactorSnapshot>;

/**
 * Common base shape returned by all computeXxxResult functions.
 * Calculators with a discriminated-union state (DailyOff, DailyRest) intersect this
 * with their own state field. Calculators without a state union return this directly.
 */
export type TComputedAvailabilityResult<TFactors extends TFactorMap = TFactorMap> = {
    isConsuming: boolean;
    nextAvailabilityEvent?: TLabeledEventFactor;
    nextExhaustionEvent?: TLabeledEventFactor;
    factors: TFactors;
};

/** Formats a conditions Record as an indented string for regression-checker error messages. */
export const describeConditions = (conditions: Record<string, unknown>): string =>
    Object.keys(conditions)
        .map(k => { const v = conditions[k]; return `    ${k}: ${v === undefined ? "undefined" : v === null ? "null" : v instanceof Date ? v.toISOString() : String(v)}`; })
        .join("\n");

/** Evaluate all factors and return both the winning event and the full snapshot in a single pass. */
export const resolveEarliestEvent = (
    factors: Record<string, { factor: TAvailabilityFactor; label: string }>
): { winner: { date: Date; label: string } | undefined; snapshot: TFactorMap } => {
    let winner: { date: Date; label: string } | undefined = undefined;
    const snapshot: TFactorMap = {};

    for (const key of Object.keys(factors)) {
        const { factor, label } = factors[key];
        const date = factor();
        snapshot[key] = { date };

        if (date !== undefined) {
            if (winner === undefined || date.getTime() < winner.date.getTime()) {
                winner = { date, label };
            }
        }
    }

    return { winner, snapshot };
};

